import { kChatHtmlToDocxPath } from '@/constants/constants-api-paths'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { sanitizeFileName } from '@/routes/dashboard/files/utils/files-utils'
import { ApiError } from '@/util/errors/api-error'
import saveAs from 'file-saver'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'

/**
 * Download sanitized HTML as a .docx file.
 *
 * @param sanitizedHtml - Already-sanitized HTML content
 * @param fileName - Name for the downloaded file
 */
export async function downloadLrrAsDocx(sanitizedHtml: string, fileName: string) {
  const token = await getAuth().currentUser?.getIdToken(true)
  if (!token) throw Error(kErrorCodeAuthError)

  const sentry_transaction_id = nanoid()

  try {
    const apiUrl = kPaxtonAppApiRESTBaseUrl() + kChatHtmlToDocxPath
    const response = await fetch(apiUrl, {
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentry_transaction_id,
        Authorization: `Bearer ${token}`,
      },
      method: 'POST',
      body: JSON.stringify({ html: sanitizedHtml }),
    })

    if (!response.ok) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(new Error(`Fetch error - status: ${response.status}, error: ${response.statusText}`), {
          extra: { requestUrl: apiUrl, onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled, fileName },
        })
      })
      throw new ApiError(response.status, `Fetch error - status: ${response.status}, error: ${response.statusText}`)
    }

    const blob = await response.blob()
    saveAs(blob, sanitizeFileName(fileName) + '.docx')
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(error, {
          extra: { onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled, fileName },
        })
      })
    }
    throw error
  }
}
